// ─── Search ad format types and configuration ──────────────────────────────
//
// Search campaigns use a completely separate path from social ads:
// different backend API (/search-assets/*), different DB table
// (campaign_search_assets), and different field structures.

import type { Campaign } from '@/api/campaigns';

// The default Ford square logo. Only env-agnostic fields live here: `path`
// resolves the preview thumbnail via /api/static/<path> (served from the
// backend's own bucket), and `contentType`. The persisted gs:// storage path is
// fetched at runtime from /api/static/default-media so it always points at the
// current environment's bucket — mirroring the rsa-defaults pattern. A
// hardcoded bucket here previously made prod downloads 403 (the prod service
// account cannot read the dev bucket cross-project).
const DEFAULT_SQUARE_LOGO = {
  path: 'content-tracker/defaults/ford-square-logo.jpg',
  contentType: 'image/jpeg',
};

// ─── Format type ────────────────────────────────────────────────────────────

export type SearchFormatType =
  | 'msan'
  | 'search-rsa'
  | 'microsoft-pmax'
  | 'google-pmax'
  | 'demand-gen'
  | 'youtube-view'
  | 'youtube-reach';

// ─── Detection helpers ──────────────────────────────────────────────────────

export function isSearchCampaign(campaign: Campaign | null | undefined): boolean {
  return campaign?.channel === 'Paid Search';
}

/**
 * Map a campaign's platform + format to a SearchFormatType.
 * Returns undefined if the format is unrecognised.
 */
export function getSearchFormatType(
  campaign: Campaign | null | undefined,
): SearchFormatType | undefined {
  if (!campaign) return undefined;

  const format = (campaign.format ?? '').toLowerCase().trim();
  const platform = (campaign.platform ?? '').toLowerCase().trim();

  if (format === 'msan') return 'msan';
  if (format === 'demand gen') return 'demand-gen';

  // YouTube — buy_type determines View (CPV) vs Reach (CPM)
  if (format === 'youtube') {
    const buyType = (campaign.buy_type ?? '').toLowerCase().trim();
    if (buyType === 'cpm') return 'youtube-reach';
    return 'youtube-view';
  }

  // "Search" format exists on both Google and Microsoft — maps to the same RSA component
  if (format === 'search') return 'search-rsa';

  // Performance Max — platform determines which variant
  if (format === 'performance max') {
    if (platform.includes('microsoft') || platform.includes('bing')) return 'microsoft-pmax';
    return 'google-pmax';
  }

  return undefined;
}

// ─── Field configuration ────────────────────────────────────────────────────
// Each format specifies which fields to show and their constraints.
// The DynamicTextFieldArray component reads these to render the right UI.

export interface DynamicFieldConfig {
  min: number;
  max: number;
  charLimit: number;
  label: string;
  /** Hint text shown below the section header */
  hint?: string;
}

export interface MediaSlotConfig {
  slot: string;
  label: string;
  min: number;
  max: number;
  dimensions: string;
  accept?: string;
  optional?: boolean;
  /** e.g. "Recommended 3+" */
  recommended?: string;
  /** Recommended count for validation warning (e.g. 5 means warn if < 5) */
  recommendedCount?: number;
  /** e.g. "Minimum 10 seconds" for YouTube videos */
  minLength?: string;
  /** Max file size in bytes (e.g. 5242880 for 5MB). Reject on upload if exceeded. */
  maxFileSize?: number;
  /** Accepted MIME types for validation (e.g. ['image/jpeg', 'image/png']) */
  acceptTypes?: string[];
  /** Accepted aspect ratios as decimal values (e.g. [1.91, 1, 0.8]). Error if file doesn't match any. */
  acceptedRatios?: number[];
  /** Recommended aspect ratios (warning only, doesn't block approval) */
  recommendedRatios?: number[];
}

/** Section types that can appear in a search ad overlay, in order. */
export type SearchSection =
  | 'about'
  | 'text'
  | 'display_url'
  | 'images'
  | 'videos'
  | 'logos'
  | 'youtube_videos'
  | 'cta'
  | 'companion_banner'
  | 'final_url'
  | 'image_extensions'
  | 'images_placeholder'
  | 'sitelinks'
  | 'legal';

export interface ConditionalWarning {
  when: 'hasImages' | 'hasVideos' | 'hasLongHeadlines' | 'hasCompanionBanner' | 'hasNeither';
  /** Text field to check for emptiness */
  textField?: 'headlines' | 'longHeadlines' | 'descriptions';
  /** Media slot(s) to check for emptiness */
  mediaSlots?: string[];
  /** Key for the fieldIssues record */
  warnKey: string;
  message: string;
}

export interface SearchFormatConfig {
  label: string;
  platform: 'Google' | 'Microsoft' | 'Google & Microsoft';
  aboutText: string;
  /** Section ordering — determines what appears and in what order */
  sections: SearchSection[];
  headlines?: DynamicFieldConfig;
  longHeadlines?: DynamicFieldConfig;
  descriptions?: DynamicFieldConfig;
  displayUrlPaths?: DynamicFieldConfig;
  businessName?: { charLimit: number; optional?: boolean };
  finalUrl: boolean;
  cta?: {
    min: number;
    max: number;
    options: string[];
    allowCustom?: boolean;
    customCharLimit?: number;
  };
  /** Media slot configs for images, logos, videos, banners — drives min/max enforcement */
  mediaSlots?: Record<string, MediaSlotConfig>;
  /** Max images across all image slots combined (e.g. 20 for PMax) */
  sharedImageMax?: number;
  /** Default media files to pre-populate on fresh load (slot -> static config).
   *  The gs:// storage path is resolved at runtime from /api/static/default-media,
   *  so it is intentionally absent here. */
  defaultMedia?: Record<string, { path: string; contentType: string }>;
  /** Format-specific validation rules beyond min/max/charLimit */
  validationRules?: {
    /** At least one headline must be ≤ this many characters */
    oneHeadlineMaxChars?: number;
    /** Warn if fewer than this many descriptions (not an error) */
    recommendedDescriptions?: number;
    /** Warn if long headline is shorter than this (not an error) */
    longHeadlineMinWarning?: number;
    /** Warn if no video provided (not an error, just recommended) */
    videoRecommended?: boolean;
  };
  /** Conditional warnings — advisory warnings that only fire when certain assets are present */
  conditionalWarnings?: ConditionalWarning[];
}

export const SEARCH_FORMAT_CONFIGS: Record<SearchFormatType, SearchFormatConfig> = {
  msan: {
    label: 'Microsoft Audience Network (MSAN)',
    platform: 'Microsoft',
    aboutText:
      'Microsoft Audience Network ads are asset-based display ads, where users provide text and creative assets which Microsoft dynamically assembles into ad formats across MSN, Outlook, Microsoft Edge, and partner websites.',
    sections: ['about', 'text', 'cta', 'images', 'videos', 'logos', 'final_url'],
    headlines: {
      min: 1,
      max: 15,
      charLimit: 40,
      label: 'Short Headlines',
      hint: 'Recommended 25 characters or less',
    },
    longHeadlines: { min: 1, max: 5, charLimit: 90, label: 'Long Headlines' },
    descriptions: { min: 1, max: 5, charLimit: 90, label: 'Ad Text', hint: 'Recommended 3+' },
    businessName: { charLimit: 25 },
    finalUrl: true,
    validationRules: { oneHeadlineMaxChars: 25 },
    cta: {
      min: 0,
      max: 1,
      options: [
        'Act now',
        'Apply now',
        'Book now',
        'Buy now',
        'Chat now',
        'Compare',
        'Contact us',
        'Dealers',
        'Directions',
        'Download',
        'Get deals',
        'Get offer',
        'Get quote',
        'Join now',
        'Order now',
        'Reserve',
        'See offers',
        'Test drive',
        'View cars',
      ],
    },
    mediaSlots: {
      image: {
        slot: 'image',
        label: 'Images',
        min: 1,
        max: 16,
        dimensions:
          'JPEG or PNG · 9 aspect ratios accepted:·1.91:1 — 1200×628 rec, 703×368 min·1.78:1 — 1200×674 rec, 624×350 min·1.72:1 — 1200×698 rec, 300×174 min·1.69:1 — 1200×710 rec, 622×368 min·1.55:1 — 1200×774 rec, 300×194 min·1.5:1 — 1200×800 rec, 300×200 min·1.33:1 — 1200×902 rec, 100×75 min·1:1 — 1200×1200 rec, 300×300 min·0.93:1 — 1116×1200 rec, 311×333 min',
        recommended: 'Recommended 5+ in different ratios',
        recommendedCount: 5,
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [1.91, 1.78, 1.72, 1.69, 1.55, 1.5, 1.33, 1, 0.93],
      },
      video: {
        slot: 'video',
        label: 'Video',
        min: 0,
        max: 5,
        dimensions:
          'File types — FLV, WAV, MOV, MP4, WMV, MPEG2, 3GP, MXF, GXF, AVI·Aspect ratio — 16:9 recommended, 9:16 also accepted·Length — 6–120 seconds (short-form 6–30s perform best)·Resolution — Min 480×270, recommended 1280×720 at 3000 kbps·Audio — Recommended 196 kbps @ 44.1 kHz AAC·File size — Max 10GB',
        accept: 'video/*',
        optional: true,
        acceptTypes: [
          'video/mp4',
          'video/quicktime',
          'video/x-msvideo',
          'video/x-ms-wmv',
          'video/x-flv',
          'video/3gpp',
          'video/mpeg',
        ],
        maxFileSize: 10737418240,
        recommendedRatios: [16 / 9, 9 / 16],
      },
      square_logo: {
        slot: 'square_logo',
        label: 'Square Logo',
        min: 0,
        max: 1,
        dimensions: 'Ratio — 1:1·Size — 128×128 pixels',
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [1],
      },
      landscape_logo: {
        slot: 'landscape_logo',
        label: 'Landscape Logo',
        min: 0,
        max: 1,
        dimensions: 'Ratio — 4:1·Size — 512×128 pixels',
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [4],
      },
    },
    defaultMedia: { square_logo: DEFAULT_SQUARE_LOGO },
  },

  'search-rsa': {
    label: 'Responsive Search Ad (RSA)',
    platform: 'Google & Microsoft',
    aboutText:
      'Responsive Search Ads are text-based ads where multiple headlines and descriptions are provided. Google and Microsoft dynamically combine them to generate different ad variations and optimize performance.',
    sections: ['about', 'text', 'final_url', 'display_url', 'image_extensions', 'sitelinks'],
    headlines: { min: 3, max: 15, charLimit: 30, label: 'Headlines' },
    descriptions: { min: 2, max: 4, charLimit: 90, label: 'Descriptions' },
    displayUrlPaths: { min: 0, max: 2, charLimit: 15, label: 'Display URL Paths' },
    finalUrl: true,
  },

  'microsoft-pmax': {
    label: 'Performance Max (Microsoft)',
    platform: 'Microsoft',
    aboutText:
      'Microsoft Performance Max ads are asset-based, meaning users provide text and creative assets which Microsoft Ads dynamically assembles into different ad formats across Bing Search, Microsoft Audience Network, Outlook, and partner sites. The preview overlay provides representative previews rather than an exact rendering of the final ad.',
    sections: ['about', 'text', 'display_url', 'images', 'logos', 'cta', 'final_url', 'sitelinks'],
    sharedImageMax: 20,
    headlines: { min: 3, max: 15, charLimit: 30, label: 'Headlines' },
    longHeadlines: { min: 1, max: 5, charLimit: 90, label: 'Long Headlines' },
    descriptions: { min: 2, max: 5, charLimit: 90, label: 'Descriptions', hint: 'Recommended 3+' },
    displayUrlPaths: { min: 0, max: 2, charLimit: 15, label: 'Display URL Paths' },
    businessName: { charLimit: 25 },
    finalUrl: true,
    validationRules: {
      oneHeadlineMaxChars: 15,
      recommendedDescriptions: 3,
      longHeadlineMinWarning: 30,
    },
    cta: {
      min: 0,
      max: 1,
      options: [
        'Act now',
        'Apply now',
        'Book now',
        'Buy now',
        'Contact us',
        'Download',
        'Get quote',
        'Learn more',
        'Order now',
        'Shop now',
        'Sign up',
        'Visit site',
      ],
    },
    mediaSlots: {
      landscape_image: {
        slot: 'landscape_image',
        label: 'Landscape Image',
        min: 1,
        max: 20,
        dimensions: 'Recommended — 1200×628·Minimum — 703×368',
        recommended: 'Recommended 3+',
        recommendedCount: 3,
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [1.91],
      },
      square_image: {
        slot: 'square_image',
        label: 'Square Image',
        min: 1,
        max: 20,
        dimensions: 'Recommended — 1200×1200·Minimum — 300×300',
        recommended: 'Recommended 3+',
        recommendedCount: 3,
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [1],
      },
      portrait_image: {
        slot: 'portrait_image',
        label: 'Portrait Image',
        min: 0,
        max: 20,
        dimensions: 'Recommended — 600×1200·Minimum — 470×940',
        optional: true,
        recommended: 'Recommended 1+',
        recommendedCount: 1,
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [0.5],
      },
      square_logo: {
        slot: 'square_logo',
        label: 'Square Logo',
        min: 1,
        max: 5,
        dimensions: 'Recommended — 1200×1200·Minimum — 128×128',
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [1],
      },
      landscape_logo: {
        slot: 'landscape_logo',
        label: 'Landscape Logo',
        min: 0,
        max: 5,
        dimensions: 'Recommended — 1200×300·Minimum — 512×128',
        optional: true,
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [4],
      },
    },
    defaultMedia: { square_logo: DEFAULT_SQUARE_LOGO },
  },

  'google-pmax': {
    label: 'Performance Max (Google)',
    platform: 'Google',
    aboutText:
      'Performance Max ads are asset-based, meaning users provide text and creative assets which Google dynamically assembles into different ad formats across Google Search, Display Network, YouTube, Gmail, and Discover. The preview overlay provides representative previews rather than an exact rendering of the final ad.',
    sections: [
      'about',
      'text',
      'display_url',
      'images',
      'logos',
      'youtube_videos',
      'cta',
      'final_url',
      'sitelinks',
    ],
    sharedImageMax: 20,
    headlines: { min: 3, max: 15, charLimit: 30, label: 'Headlines' },
    longHeadlines: { min: 1, max: 5, charLimit: 90, label: 'Long Headlines' },
    descriptions: { min: 2, max: 5, charLimit: 90, label: 'Descriptions' },
    displayUrlPaths: { min: 0, max: 2, charLimit: 15, label: 'Display URL Paths' },
    businessName: { charLimit: 25 },
    finalUrl: true,
    validationRules: {
      oneHeadlineMaxChars: 15,
      recommendedDescriptions: 4,
      longHeadlineMinWarning: 30,
    },
    cta: {
      min: 0,
      max: 1,
      options: [
        'Apply now',
        'Book now',
        'Contact us',
        'Download',
        'Learn more',
        'Visit site',
        'Shop now',
        'Sign up',
        'Get quote',
        'Subscribe',
        'See more',
      ],
    },
    mediaSlots: {
      landscape_image: {
        slot: 'landscape_image',
        label: 'Landscape Image',
        min: 1,
        max: 20,
        dimensions: 'Recommended — 1200×628·Minimum — 600×314',
        recommended: 'Recommended 3+',
        recommendedCount: 3,
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [1.91],
      },
      square_image: {
        slot: 'square_image',
        label: 'Square Image',
        min: 1,
        max: 20,
        dimensions: 'Recommended — 1200×1200·Minimum — 300×300',
        recommended: 'Recommended 3+',
        recommendedCount: 3,
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [1],
      },
      portrait_image: {
        slot: 'portrait_image',
        label: 'Portrait Image',
        min: 0,
        max: 20,
        dimensions: 'Recommended — 960×1200·Minimum — 470×940',
        optional: true,
        recommended: 'Recommended 1+',
        recommendedCount: 1,
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [0.8],
      },
      square_logo: {
        slot: 'square_logo',
        label: 'Square Logo',
        min: 1,
        max: 5,
        dimensions: 'Recommended — 1200×1200·Minimum — 128×128',
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [1],
      },
      landscape_logo: {
        slot: 'landscape_logo',
        label: 'Landscape Logo',
        min: 0,
        max: 5,
        dimensions: 'Recommended — 1200×300·Minimum — 512×128',
        optional: true,
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [4],
      },
      youtube_video: {
        slot: 'youtube_video',
        label: 'YouTube Video',
        min: 1,
        max: 5,
        dimensions: '',
        minLength: 'Minimum 10 seconds',
      },
    },
    defaultMedia: { square_logo: DEFAULT_SQUARE_LOGO },
  },

  'demand-gen': {
    label: 'Demand Gen',
    platform: 'Google',
    aboutText:
      'Demand Gen ads are asset-based, meaning users provide text and creative assets which Google dynamically assembles into different ad formats across YouTube In-Feed, YouTube Shorts, Google Discover, and Gmail. The preview overlay provides representative placement previews, not an exact rendering of the final ad.',
    sections: [
      'about',
      'text',
      'images',
      'logos',
      'youtube_videos',
      'companion_banner',
      'cta',
      'final_url',
    ],
    sharedImageMax: 20,
    headlines: {
      min: 1,
      max: 5,
      charLimit: 40,
      label: 'Headlines',
      hint: 'Recommended 30 or less for optimal placement display',
    },
    longHeadlines: {
      min: 0,
      max: 5,
      charLimit: 90,
      label: 'Long Headlines',
      hint: 'Video placement only',
    },
    descriptions: { min: 1, max: 5, charLimit: 90, label: 'Descriptions' },
    businessName: { charLimit: 25, optional: true },
    finalUrl: true,
    validationRules: { oneHeadlineMaxChars: 30 },
    cta: {
      min: 1,
      max: 1,
      options: [
        'Apply now',
        'Book now',
        'Contact us',
        'Download',
        'Learn more',
        'Visit site',
        'Shop now',
        'Sign up',
        'Get quote',
        'Subscribe',
        'See more',
      ],
    },
    mediaSlots: {
      landscape_image: {
        slot: 'landscape_image',
        label: 'Landscape Image',
        min: 0,
        max: 20,
        dimensions: 'Recommended — 1200×628·Minimum — 600×314',
        optional: true,
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [1.91],
      },
      square_image: {
        slot: 'square_image',
        label: 'Square Image',
        min: 0,
        max: 20,
        dimensions: 'Recommended — 1200×1200·Minimum — 300×300',
        optional: true,
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [1],
      },
      portrait_image: {
        slot: 'portrait_image',
        label: 'Portrait Image',
        min: 0,
        max: 20,
        dimensions: 'Recommended — 960×1200·Minimum — 480×600',
        optional: true,
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5242880,
        acceptedRatios: [0.8],
      },
      square_logo: {
        slot: 'square_logo',
        label: 'Square Logo',
        min: 1,
        max: 5,
        dimensions: 'Ratio — 1:1·Recommended — 1200×1200·Minimum — 144×144',
        acceptTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 153600,
        acceptedRatios: [1],
      },
      youtube_video: {
        slot: 'youtube_video',
        label: 'YouTube Video',
        min: 0,
        max: 5,
        dimensions: '',
        minLength: 'Minimum 5 seconds',
      },
      companion_banner: {
        slot: 'companion_banner',
        label: 'Companion Banner',
        min: 0,
        max: 1,
        dimensions: 'Size — 300×60·Format — JPG, PNG or GIF·File size — Max 150 KB',
        optional: true,
        acceptTypes: ['image/jpeg', 'image/png', 'image/gif'],
        maxFileSize: 153600,
        acceptedRatios: [5],
      },
    },
    defaultMedia: { square_logo: DEFAULT_SQUARE_LOGO },
    conditionalWarnings: [
      {
        when: 'hasImages',
        textField: 'headlines',
        warnKey: 'headlines',
        message:
          'Please add at least 1 headline — ignore if your ad format does not require an image',
      },
      {
        when: 'hasImages',
        textField: 'descriptions',
        warnKey: 'descriptions',
        message:
          'Please add at least 1 description — ignore if your ad format does not require an image',
      },
      {
        when: 'hasVideos',
        textField: 'longHeadlines',
        warnKey: 'long_headlines',
        message:
          'Please add at least 1 long headline — ignore if your ad format does not require video',
      },
      {
        when: 'hasVideos',
        mediaSlots: ['companion_banner'],
        warnKey: 'media_companion_banner',
        message:
          'Remember to add a companion banner — ignore if your ad format does not require video',
      },
      {
        when: 'hasLongHeadlines',
        mediaSlots: ['youtube_video'],
        warnKey: 'media_youtube_video',
        message: 'Remember to add a YouTube video when using long headlines',
      },
      {
        when: 'hasLongHeadlines',
        mediaSlots: ['companion_banner'],
        warnKey: 'media_companion_banner',
        message: 'Remember to add a companion banner when using long headlines',
      },
      {
        when: 'hasCompanionBanner',
        mediaSlots: ['youtube_video'],
        warnKey: 'media_youtube_video',
        message: 'Remember to add a YouTube video when using a companion banner',
      },
      {
        when: 'hasCompanionBanner',
        textField: 'longHeadlines',
        warnKey: 'long_headlines',
        message: 'Please add at least 1 long headline when using a companion banner',
      },
      {
        when: 'hasNeither',
        mediaSlots: ['landscape_image', 'square_image', 'portrait_image'],
        warnKey: 'images_conditional',
        message:
          'Remember to add an image asset — ignore if your ad format does not require an image',
      },
      {
        when: 'hasNeither',
        mediaSlots: ['youtube_video'],
        warnKey: 'media_youtube_video',
        message: 'Remember to add a video asset — ignore if your ad format does not require video',
      },
    ],
  },

  'youtube-view': {
    label: 'YouTube Video View',
    platform: 'Google',
    aboutText:
      'YouTube Video View campaigns are designed to drive conversions, website traffic, or engagement. These ads use a video as the primary asset, supported by headlines, descriptions, and call-to-action buttons. Google dynamically assembles these assets across YouTube In-Stream, Shorts, In-Feed, and Search placements.',
    sections: [
      'about',
      'youtube_videos',
      'text',
      'cta',
      'companion_banner',
      'final_url',
      'display_url',
    ],
    headlines: { min: 1, max: 5, charLimit: 30, label: 'Short Headlines' },
    longHeadlines: { min: 1, max: 5, charLimit: 90, label: 'Long Headlines' },
    descriptions: { min: 1, max: 5, charLimit: 90, label: 'Descriptions', hint: 'Recommended 3+' },
    displayUrlPaths: { min: 0, max: 2, charLimit: 15, label: 'Display URL Paths' },
    businessName: { charLimit: 25, optional: true },
    finalUrl: true,
    validationRules: { recommendedDescriptions: 3 },
    cta: {
      min: 1,
      max: 5,
      options: [
        'Apply now',
        'Book now',
        'Contact us',
        'Download',
        'Learn more',
        'Visit site',
        'Shop now',
        'Sign up',
        'Get quote',
        'Subscribe',
        'See more',
      ],
      allowCustom: true,
      customCharLimit: 10,
    },
    mediaSlots: {
      youtube_video: {
        slot: 'youtube_video',
        label: 'YouTube Video',
        min: 1,
        max: 5,
        dimensions: '',
      },
      companion_banner: {
        slot: 'companion_banner',
        label: 'Companion Banner',
        min: 1,
        max: 1,
        dimensions: 'Size — 300×60·Format — JPG / PNG / GIF·File size — Max 150 KB',
        accept: 'image/jpeg,image/png,image/gif',
        acceptTypes: ['image/jpeg', 'image/png', 'image/gif'],
        maxFileSize: 153600,
        acceptedRatios: [5],
      },
    },
  },

  'youtube-reach': {
    label: 'YouTube Video Reach',
    platform: 'Google',
    aboutText:
      'YouTube Video Reach campaigns are designed to maximise brand reach and awareness. These ads use a video as the primary asset with a single headline, description, and optional CTA. They appear across skippable and non-skippable in-stream ads, bumper ads, and YouTube Shorts.',
    sections: [
      'about',
      'youtube_videos',
      'text',
      'cta',
      'companion_banner',
      'final_url',
      'display_url',
    ],
    headlines: {
      min: 1,
      max: 1,
      charLimit: 90,
      label: 'Headline',
      hint: 'Recommended 25 characters or less for optimal placement display',
    },
    descriptions: { min: 1, max: 1, charLimit: 90, label: 'Description' },
    displayUrlPaths: { min: 1, max: 1, charLimit: 255, label: 'Display URL' },
    finalUrl: true,
    validationRules: { oneHeadlineMaxChars: 25 },
    cta: {
      min: 0,
      max: 1,
      options: [
        'Apply now',
        'Book now',
        'Contact us',
        'Download',
        'Learn more',
        'Visit site',
        'Shop now',
        'Sign up',
      ],
      allowCustom: true,
      customCharLimit: 10,
    },
    mediaSlots: {
      youtube_video: {
        slot: 'youtube_video',
        label: 'YouTube Video',
        min: 1,
        max: 5,
        dimensions: '',
      },
      companion_banner: {
        slot: 'companion_banner',
        label: 'Companion Banner',
        min: 1,
        max: 1,
        dimensions: 'Size — 300×60·Format — JPG / PNG / GIF·File size — Max 150 KB',
        accept: 'image/jpeg,image/png,image/gif',
        acceptTypes: ['image/jpeg', 'image/png', 'image/gif'],
        maxFileSize: 153600,
        acceptedRatios: [5],
      },
    },
  },
};
